import { differenceInMinutes } from 'date-fns';
import { LUNCH_BREAK } from 'containers/pages/payrollReport/constants';
import { GuardPayrollRecord, ConsultantPayrollRecord, DateGroupRow } from 'containers/pages/payrollReport/types';
import { minToHourMin, decimalToHourMin } from './dateUtils';

/**
 * Returns 0 if the address is "Lunch Break", otherwise returns the minutes between start and end.
 */
export const lunchCheck = (startTime: string, endTime: string, address: string): number => {
  if (address === LUNCH_BREAK) return 0;
  return differenceInMinutes(new Date(endTime), new Date(startTime));
};

/**
 * Calculates total hours for a group of guard records as "Xh Ym".
 */
export const calculateGuardTotalHours = (records: GuardPayrollRecord[]): string => {
  const totalMinutes = records.reduce(
    (sum, r) => sum + lunchCheck(r.StartTime, r.EndTime, r.Address),
    0,
  );
  return minToHourMin(totalMinutes);
};

/**
 * Calculates regular and overtime hours for a group of consultant records.
 */
export const calculateConsultantHours = (
  records: ConsultantPayrollRecord[],
): { regularTime: string; overTime: string } => {
  const totalRT = records.reduce((sum, r) => sum + parseFloat(r.CommentTimeRegular || '0'), 0);
  const totalOT = records.reduce((sum, r) => sum + parseFloat(r.CommentTimeOver || '0'), 0);
  return {
    regularTime: decimalToHourMin(totalRT),
    overTime: decimalToHourMin(totalOT),
  };
};

/**
 * Calculates grand total hours across all guard date groups.
 */
export const calculateGuardGrandTotal = (dateGroups: DateGroupRow[]): string => {
  let totalMinutes = 0;
  dateGroups.forEach((group) => {
    const match = group.totalHours.match(/(\d+)h\s+(\d+)m/);
    if (match) {
      totalMinutes += parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
    }
  });
  return minToHourMin(totalMinutes);
};

/**
 * Calculates grand total regular and overtime across all consultant date groups.
 */
export const calculateConsultantGrandTotal = (
  dateGroups: DateGroupRow[],
): string => {
  let totalRTMinutes = 0;
  let totalOTMinutes = 0;

  dateGroups.forEach((group) => {
    const rtMatch = group.totalHoursRT?.match(/(\d+)h\s+(\d+)m/);
    const otMatch = group.totalHoursOT?.match(/(\d+)h\s+(\d+)m/);
    if (rtMatch) {
      totalRTMinutes += parseInt(rtMatch[1], 10) * 60 + parseInt(rtMatch[2], 10);
    }
    if (otMatch) {
      totalOTMinutes += parseInt(otMatch[1], 10) * 60 + parseInt(otMatch[2], 10);
    }
  });

  return `RT: ${minToHourMin(totalRTMinutes)}, OT: ${minToHourMin(totalOTMinutes)}`;
};
